import React, { useState, useEffect } from 'react';
import { AppDrawer } from '../ui/AppDrawer';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { vehicleToWarehouseAPI, warehouseAPI } from '../../lib/api';

export const ReturnToWarehouseModal = ({ 
  open, 
  onClose, 
  vanId, 
  vanName,
  inventory = [],
  preSelectedItemId = null 
}) => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [destinationId, setDestinationId] = useState('');
  const [itemId, setItemId] = useState(preSelectedItemId || '');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      fetchWarehouses();
      setItemId(preSelectedItemId || '');
      setQuantity('1');
      setNote('');
    }
  }, [open, preSelectedItemId]);

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const response = await warehouseAPI.get();
      setWarehouses([response.data]);
      if (response.data) setDestinationId(response.data.id);
    } catch (error) {
      toast.error('Failed to load warehouse locations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!destinationId) { toast.error('Please select a destination warehouse'); return; }
    if (!itemId) { toast.error('Please select an item'); return; }
    if (!quantity || parseInt(quantity) < 1) { toast.error('Quantity must be at least 1'); return; }
    const selectedItem = inventory.find(i => i.item_id === itemId);
    if (selectedItem && parseInt(quantity) > selectedItem.qty_on_hand) {
      toast.error(`Cannot return more than available (${selectedItem.qty_on_hand})`);
      return;
    }

    setSubmitting(true);
    try {
      await vehicleToWarehouseAPI.create({
        source_van_id: vanId,
        destination_location_id: destinationId,
        item_id: itemId,
        quantity: parseInt(quantity),
        note: note || null,
      });
      toast.success('Return request submitted for approval');
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit return request');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedItem = inventory.find(i => i.item_id === itemId);
  const maxQty = selectedItem?.qty_on_hand || 0;
  const isDirty = itemId !== '' || note !== '';

  return (
    <AppDrawer
      open={open}
      onOpenChange={(nextOpen) => { if (!nextOpen) onClose(false); }}
      title={
        <span className="flex items-center gap-2">
          <Undo2 className="w-5 h-5" />
          Return to Warehouse
        </span>
      }
      description={`Request to return items from ${vanName || 'vehicle'} to warehouse. Requires owner approval.`}
      size="md"
      isDirty={isDirty}
      data-testid="return-to-warehouse-drawer"
      footer={
        <>
          <Button variant="outline" onClick={() => onClose(false)} disabled={submitting} data-testid="return-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="return-submit-btn">
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
            ) : (
              'Submit Request'
            )}
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Destination Warehouse */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Destination Warehouse</Label>
            <Select value={destinationId} onValueChange={setDestinationId}>
              <SelectTrigger className="h-12" data-testid="destination-select">
                <SelectValue placeholder="Select warehouse" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((wh) => (
                  <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Item Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Item</Label>
            <Select value={itemId} onValueChange={setItemId}>
              <SelectTrigger className="h-12" data-testid="item-select">
                <SelectValue placeholder="Select item to return" />
              </SelectTrigger>
              <SelectContent>
                {inventory.filter(i => i.qty_on_hand > 0).map((inv) => (
                  <SelectItem key={inv.item_id} value={inv.item_id}>
                    {inv.item_name} ({inv.qty_on_hand} available)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Quantity</Label>
            <Input type="number" min="1" max={maxQty} value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="h-12 text-lg font-mono" data-testid="quantity-input" />
            {selectedItem && (
              <p className="text-xs text-muted-foreground">Max: {maxQty} units available</p>
            )}
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Reason/Note (optional)</Label>
            <Textarea placeholder="Why are you returning these items?"
              value={note} onChange={(e) => setNote(e.target.value)}
              rows={3} className="resize-none" data-testid="note-input" />
          </div>
        </div>
      )}
    </AppDrawer>
  );
};
